/**
 * Spring cron parser → human-readable text.
 *
 * Spring cron has 6 fields: `second minute hour day-of-month month day-of-week`.
 * Quartz-style `?` is accepted as "no value" (treated as `*`).
 *
 * The parser covers the patterns we actually ship in service config:
 *   `0 STAR/N * * * ?`     Every N minutes
 *   `0 0 H * * *`          At HH:00 every day
 *   `0 0 H,H,H * * *`      At each listed HH:00 every day
 *   `0 M H * * *`          At HH:MM every day
 *   `0 0 H * * MON-FRI`    At HH:00 on weekdays
 *   `0 0 H * * SAT,SUN`    At HH:00 on weekends
 *   `0 0 H * * MON`        At HH:00 every Monday
 *   `0 0 H D * *`          At HH:00 on day D of the month
 *
 * Everything else falls through to `Cron: <expr>` so the user can still read
 * (and copy) the raw expression while we describe more patterns over time.
 */

const DAY_NAMES: Record<string, string> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  '7': 'Sunday',
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
}

const CRON_FIELD = /^[0-9*/,?\-A-Z]+$/i

/** Loose detector: 5- or 6-field whitespace-separated cron-character string. */
export function looksLikeCronExpression(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  const parts = trimmed.split(/\s+/)
  if (parts.length < 5 || parts.length > 6) return false
  return parts.every((p) => CRON_FIELD.test(p))
}

function pad2(n: number | string): string {
  const s = String(n)
  return s.length < 2 ? `0${s}` : s
}

function describeHour(field: string): string | null {
  // Whole-hour list: "18,22"
  if (/^\d+(?:,\d+)*$/.test(field)) {
    const hours = field.split(',').map((h) => pad2(h))
    if (hours.length === 1) return `${hours[0]}:00`
    if (hours.length === 2) return `${hours[0]}:00 and ${hours[1]}:00`
    return `${hours.slice(0, -1).join(', ')}:00 and ${hours[hours.length - 1]}:00`
  }
  return null
}

function describeDayOfWeek(field: string): string {
  const v = field.toUpperCase()
  if (v === '*' || v === '?') return ''
  if (v === 'MON-FRI' || v === '1-5') return ' on weekdays'
  if (v === 'SAT,SUN' || v === '6,0' || v === '0,6' || v === '6,7') return ' on weekends'
  if (DAY_NAMES[v]) return ` every ${DAY_NAMES[v]}`
  if (/^[A-Z]+(?:,[A-Z]+)+$/.test(v)) {
    const names = v.split(',').map((d) => DAY_NAMES[d] || d)
    return ` on ${names.join(', ')}`
  }
  return ` (DOW: ${field})`
}

export function describeCronExpression(expr: string): string | null {
  if (typeof expr !== 'string') return null
  const trimmed = expr.trim()
  if (!trimmed) return null

  const parts = trimmed.split(/\s+/)
  // Spring is 6-field; accept 5-field by prepending `0` for seconds.
  const normalized = parts.length === 5 ? ['0', ...parts] : parts
  if (normalized.length !== 6) return null

  const [sec, min, hour, dom, , dow] = normalized

  // `Every N minutes` — second 0, minute */N, hour any, no day filter.
  const everyN = /^\*\/(\d+)$/.exec(min)
  if (sec === '0' && everyN && (hour === '*' || hour === '*/1')) {
    const n = parseInt(everyN[1], 10)
    return n === 1 ? 'Every minute' : `Every ${n} minutes`
  }

  // Hour every N: `0 0 */N * * ?`
  const hourEveryN = /^\*\/(\d+)$/.exec(hour)
  if (sec === '0' && min === '0' && hourEveryN) {
    const n = parseInt(hourEveryN[1], 10)
    return n === 1 ? 'Every hour' : `Every ${n} hours`
  }

  // Specific times every day / every weekday / etc.
  if (sec === '0' && /^\d+$/.test(min) && /^\d+(?:,\d+)*$/.test(hour)) {
    const m = pad2(min)
    const hoursList = hour
      .split(',')
      .map((h) => `${pad2(h)}:${m}`)
    const timesLabel =
      hoursList.length === 1
        ? hoursList[0]
        : hoursList.length === 2
        ? `${hoursList[0]} and ${hoursList[1]}`
        : `${hoursList.slice(0, -1).join(', ')} and ${hoursList[hoursList.length - 1]}`

    const dowLabel = describeDayOfWeek(dow)
    if (dom === '*' || dom === '?') {
      // Day-of-month wildcard ⇒ runs every (matching) day.
      const base = `At ${timesLabel}`
      return dowLabel ? `${base}${dowLabel}` : `${base} every day`
    }
    if (/^\d+$/.test(dom)) {
      return `At ${timesLabel} on day ${dom} of every month`
    }
  }

  // Whole-hour pattern with min explicitly 0, no list. e.g. `0 0 H * * *`
  if (sec === '0' && min === '0') {
    const hoursLabel = describeHour(hour)
    if (hoursLabel) {
      const dowLabel = describeDayOfWeek(dow)
      const base = `At ${hoursLabel}`
      if (dom === '*' || dom === '?') {
        return dowLabel ? `${base}${dowLabel}` : `${base} every day`
      }
      if (/^\d+$/.test(dom)) {
        return `${base} on day ${dom} of every month`
      }
    }
  }

  return null
}
